/**
 * TIME Watch — Utility → suono d'allarme condiviso per Timer, Promemoria,
 * Sveglia e Cronometro.
 *
 * Riproduce un pattern ciclico di beep (Web Audio API) fino a stop().
 *
 * API principali:
 *  - `startAlarmLoop(ctx, groupSizes, options?)` → loop ciclico (Timer/Promemoria).
 *    `groupSizes` è un array di N beep per gruppo. Esempi:
 *      Timer      → [1]        (beep — beep — beep …)
 *      Promemoria → [2]        (beep beep — beep beep — beep beep …)
 *  - `startRingtone(ctx, ringtoneKey)` → loop suoneria per la Sveglia,
 *    con timbri e pattern differenti selezionabili dall'utente.
 *  - `playBeepsOnce(ctx, count, options?)` → riproduce N beep una sola volta
 *    (usato dal Cronometro alla chiusura/arresto).
 *
 * Ritorna sempre una funzione stop() idempotente.
 *
 * NUOVA REGOLA (Sprint corrente): pattern di default aggiornati
 *  - Timer      → 1 beep
 *  - Promemoria → 2 beep
 *  - Cronometro → 3 beep singoli all'arresto (playBeepsOnce)
 *  - Sveglia    → suoneria selezionabile (startRingtone)
 */

const BEEP_DUR = 0.18; // durata singolo beep (s)
const BEEP_GAP = 0.12; // pausa fra beep dentro allo stesso gruppo
const GROUP_PAUSE = 0.6; // pausa fra gruppi
const CYCLE_PAUSE = 1.1; // pausa fra fine pattern e nuovo ciclo
const BASE_FREQ = 880; // Hz — tono pulito, riconoscibile
const BASE_VOL = 0.35;

export function createAudioContext() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    return new AC();
  } catch {
    return null;
  }
}

// ── Ringtones integrate per la Sveglia ─────────────────────────────
// Ogni suoneria definisce un timbro e un pattern ciclico.
// - `type`: onda oscillatore (sine/triangle/square)
// - `freqs`: array di frequenze in Hz (una per beep). Se il pattern richiede più
//   beep delle frequenze disponibili, il pattern gira in loop sull'array.
// - `pattern`: array di gruppi di beep (come `groupSizes` di startAlarmLoop).
// - `beepDur`, `beepGap`, `groupPause`, `cyclePause`: durate personalizzate (opzionali).
export const RINGTONES = {
  classica: {
    key: "classica",
    label: "Classica",
    description: "Beep tradizionale a tre gruppi.",
    type: "sine",
    freqs: [880],
    pattern: [3, 3, 3],
  },
  digitale: {
    key: "digitale",
    label: "Digitale",
    description: "Beep rapidi ad alta frequenza.",
    type: "square",
    freqs: [1200],
    pattern: [4, 4],
    beepDur: 0.12,
    beepGap: 0.08,
    groupPause: 0.5,
    cyclePause: 0.9,
  },
  gentile: {
    key: "gentile",
    label: "Gentile",
    description: "Tono morbido e discreto.",
    type: "sine",
    freqs: [660],
    pattern: [2, 2, 2, 2],
    beepDur: 0.22,
    beepGap: 0.14,
    groupPause: 0.7,
    cyclePause: 1.3,
  },
  melodia: {
    key: "melodia",
    label: "Melodia",
    description: "Sequenza ascendente di note.",
    type: "triangle",
    // Do — Mi — Sol — Do (una ottava sopra)
    freqs: [523, 659, 784, 1047],
    pattern: [4],
    beepDur: 0.24,
    beepGap: 0.06,
    groupPause: 0.3,
    cyclePause: 1.1,
  },
  onda: {
    key: "onda",
    label: "Onda",
    description: "Alternanza tra tono alto e basso.",
    type: "sine",
    freqs: [988, 494, 988, 494],
    pattern: [4, 4],
    beepDur: 0.2,
    beepGap: 0.1,
    groupPause: 0.6,
    cyclePause: 1.0,
  },
  campana: {
    key: "campana",
    label: "Campana",
    description: "Rintocchi lenti e ampi.",
    type: "triangle",
    freqs: [523, 392],
    pattern: [1, 1, 1],
    beepDur: 0.55,
    beepGap: 0.3,
    groupPause: 0.9,
    cyclePause: 1.4,
  },
};

export const RINGTONE_LIST = Object.values(RINGTONES);
export const DEFAULT_RINGTONE_KEY = "classica";

// Scheduler interno di un singolo beep.
function scheduleBeep(ctx, master, t, freq, type = "sine", dur = BEEP_DUR, vol = BASE_VOL) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(vol, t + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(gain).connect(master);
  osc.start(t);
  osc.stop(t + dur + 0.03);
}

/**
 * Avvia il loop del pattern. Ritorna una funzione stop().
 * Chiamare stop() più volte è sicuro.
 *
 * @param {AudioContext} ctx
 * @param {number[]} groupSizes  Es. [1], [2], [3, 3, 3]
 * @param {object} [options]     { freq, type, beepDur, beepGap, groupPause, cyclePause, freqs }
 */
export function startAlarmLoop(ctx, groupSizes, options = {}) {
  if (!ctx) return () => {};
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }

  const beepDur = options.beepDur ?? BEEP_DUR;
  const beepGap = options.beepGap ?? BEEP_GAP;
  const groupPause = options.groupPause ?? GROUP_PAUSE;
  const cyclePause = options.cyclePause ?? CYCLE_PAUSE;
  const type = options.type ?? "sine";
  const freqs =
    Array.isArray(options.freqs) && options.freqs.length > 0
      ? options.freqs
      : [options.freq ?? BASE_FREQ];

  const master = ctx.createGain();
  master.gain.value = 1;
  master.connect(ctx.destination);

  let stopped = false;
  let timerId = null;
  let nextCycleAt = ctx.currentTime + 0.02;
  let beepIndex = 0;

  const scheduleCycle = () => {
    if (stopped) return;
    let cursor = nextCycleAt;
    groupSizes.forEach((count, idx) => {
      for (let i = 0; i < count; i++) {
        const f = freqs[beepIndex % freqs.length];
        scheduleBeep(ctx, master, cursor, f, type, beepDur);
        beepIndex += 1;
        cursor += beepDur + beepGap;
      }
      cursor -= beepGap;
      if (idx < groupSizes.length - 1) cursor += groupPause;
    });
    cursor += cyclePause;
    nextCycleAt = cursor;
    const delayMs = Math.max(50, (nextCycleAt - ctx.currentTime - 0.25) * 1000);
    timerId = setTimeout(scheduleCycle, delayMs);
  };

  timerId = setTimeout(scheduleCycle, 0);

  return function stop() {
    if (stopped) return;
    stopped = true;
    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }
    try {
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.linearRampToValueAtTime(0.0001, t + 0.05);
      setTimeout(() => {
        try {
          master.disconnect();
        } catch {
          /* ignore */
        }
      }, 200);
    } catch {
      /* ignore */
    }
  };
}

/**
 * Avvia una suoneria selezionabile (Sveglia).
 * @param {AudioContext} ctx
 * @param {string} ringtoneKey  chiave in RINGTONES (default: 'classica')
 */
export function startRingtone(ctx, ringtoneKey) {
  const cfg =
    RINGTONES[ringtoneKey] || RINGTONES[DEFAULT_RINGTONE_KEY];
  return startAlarmLoop(ctx, cfg.pattern, {
    type: cfg.type,
    freqs: cfg.freqs,
    beepDur: cfg.beepDur,
    beepGap: cfg.beepGap,
    groupPause: cfg.groupPause,
    cyclePause: cfg.cyclePause,
  });
}

/**
 * Riproduce N beep una sola volta (nessun loop).
 * Usato dal Cronometro: 3 beep singoli all'arresto/chiusura.
 * Ritorna comunque una funzione stop() per interrompere anzitempo.
 */
export function playBeepsOnce(ctx, count = 3, options = {}) {
  if (!ctx) return () => {};
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  const beepDur = options.beepDur ?? BEEP_DUR;
  const beepGap = options.beepGap ?? BEEP_GAP;
  const type = options.type ?? "sine";
  const freq = options.freq ?? BASE_FREQ;

  const master = ctx.createGain();
  master.gain.value = 1;
  master.connect(ctx.destination);

  let stopped = false;
  let cursor = ctx.currentTime + 0.02;
  for (let i = 0; i < count; i++) {
    scheduleBeep(ctx, master, cursor, freq, type, beepDur);
    cursor += beepDur + beepGap;
  }

  // Disconnetti il master a fine sequenza per liberare risorse.
  const totalMs = Math.max(150, (cursor - ctx.currentTime + 0.15) * 1000);
  const cleanupId = setTimeout(() => {
    try {
      master.disconnect();
    } catch {
      /* ignore */
    }
  }, totalMs);

  return function stop() {
    if (stopped) return;
    stopped = true;
    clearTimeout(cleanupId);
    try {
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.linearRampToValueAtTime(0.0001, t + 0.05);
      setTimeout(() => {
        try {
          master.disconnect();
        } catch {
          /* ignore */
        }
      }, 200);
    } catch {
      /* ignore */
    }
  };
}
